import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NotificationTemplates({ filterType = null }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/notification-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates);
    } catch (error) {
      console.error('Error fetching templates', error);
      toast.error('Fehler beim Laden der Templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const initializeDefaults = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/notification-templates/init-defaults`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      fetchTemplates();
    } catch (error) {
      console.error('Error initializing templates', error);
      toast.error('Fehler beim Erstellen der Standard-Templates');
    }
  };

  const updateTemplate = async (templateId, data) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notification-templates/${templateId}`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template aktualisiert');
      fetchTemplates();
      setEditingTemplate(null);
    } catch (error) {
      console.error('Error updating template', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const toggleActive = async (templateId, currentActive) => {
    await updateTemplate(templateId, { active: !currentActive });
  };

  const templateLabels = {
    failed_reload_customer_email: '📧 Kunde: Email bei fehlgeschlagenem Reload',
    failed_reload_customer_whatsapp: '📱 Kunde: WhatsApp bei fehlgeschlagenem Reload',
    failed_reload_admin_email: '👨‍💼 Admin: Email bei fehlgeschlagenem Reload'
  };

  if (loading) {
    return <div className="p-4">Lädt Templates...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold">Benachrichtigungs-Templates</h2>
          <p className="text-sm text-gray-600">Verwalte Email & WhatsApp Vorlagen</p>
        </div>
        {templates.length === 0 && (
          <Button onClick={initializeDefaults}>
            Standard-Templates erstellen
          </Button>
        )}
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-gray-600 mb-4">Keine Templates vorhanden</p>
            <Button onClick={initializeDefaults}>
              Standard-Templates erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {templates
            .filter(template => {
              if (!filterType) return true;
              if (filterType === 'email') {
                return template.template_type.includes('email');
              }
              if (filterType === 'whatsapp') {
                return template.template_type.includes('whatsapp');
              }
              return true;
            })
            .map(template => (
            <Card key={template.id} className={!template.active ? 'opacity-60' : ''}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CardTitle className="text-base">
                      {templateLabels[template.template_type] || template.template_type}
                    </CardTitle>
                    {template.active ? (
                      <Badge className="bg-green-100 text-green-800">Aktiv</Badge>
                    ) : (
                      <Badge variant="outline">Inaktiv</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={template.active}
                      onCheckedChange={() => toggleActive(template.id, template.active)}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingTemplate(template)}
                    >
                      Bearbeiten
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {editingTemplate?.id === template.id ? (
                <CardContent className="space-y-4">
                  {template.subject && (
                    <div>
                      <Label>Betreff</Label>
                      <Input
                        value={editingTemplate.subject}
                        onChange={(e) => setEditingTemplate({
                          ...editingTemplate,
                          subject: e.target.value
                        })}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <div>
                    <Label>Nachricht</Label>
                    <textarea
                      value={editingTemplate.body}
                      onChange={(e) => setEditingTemplate({
                        ...editingTemplate,
                        body: e.target.value
                      })}
                      rows={8}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Variablen: {'{kunde_name}'}, {'{abo_name}'}, {'{betrag}'}, {'{fehler_grund}'}, {'{datum}'}, {'{failed_count}'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => updateTemplate(editingTemplate.id, {
                        subject: editingTemplate.subject,
                        body: editingTemplate.body
                      })}
                    >
                      Speichern
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingTemplate(null)}
                    >
                      Abbrechen
                    </Button>
                  </div>
                </CardContent>
              ) : (
                <CardContent>
                  {template.subject && (
                    <p className="text-sm mb-2">
                      <strong>Betreff:</strong> {template.subject}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {template.body.substring(0, 150)}...
                  </p>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default NotificationTemplates;
